import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';
import { toast } from 'sonner';
import { api } from '@/lib/api.ts';
import { useLogin } from '@/hooks/auth-session.ts';
import { currentUserQueryOptions } from '@/hooks/queries/auth.ts';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/password-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { SudoLoginPanel } from './+components/sudo-login-panel.tsx';

export const Route = createFileRoute('/login/')({
  component: LoginPage,
  staticData: { label: msg`Log in` },
  validateSearch: z.object({
    redirect: z.string().optional(),
    error: z.enum(['no_access']).optional(),
  }),
  beforeLoad: async ({ context }) => {
    // Prime the current user so the page can decide what to show. A logged-in
    // plain park admin has no business on /login → send them to the app. Staff
    // stay (even if they also have a parkadmin profile) so they can reach the
    // sudo panel; anonymous visitors stay for the login form.
    const user = await context.queryClient.ensureQueryData(currentUserQueryOptions());
    if (user?.features.parkadmin && !user.is_staff) throw redirect({ to: '/' });
  },
});

function LoginPage() {
  const { redirect: redirectTo, error } = Route.useSearch();
  const { t } = useLingui();
  const navigate = useNavigate();
  // Drives the sudo panel off the cached identity, not local state: the login
  // mutation (and the route's beforeLoad) seed this query, so a staff user — freshly
  // logged in here, already authenticated, or bounced from a `_logged` route —
  // sees the panel without re-entering credentials.
  const { data: currentUser } = useQuery(currentUserQueryOptions());
  const isStaff = currentUser?.is_staff ?? false;

  const loginMutation = useLogin();

  const loginSchema = z.object({
    username: z.string().min(1, t`Username is required`),
    password: z.string().min(1, t`Password is required`),
  });

  const form = useForm({
    defaultValues: { username: '', password: '' },
    validators: { onSubmit: loginSchema },
    onSubmit: async ({ value }) => {
      try {
        const user = await loginMutation.mutateAsync(value);
        // Staff stay here — seeding the user query flips the render to the sudo
        // panel. Everyone else goes to the app (a parkadmin lands on the
        // dashboard; a no-access account is bounced back with the error).
        if (!user.is_staff) await navigate({ to: redirectTo ?? '/' });
      } catch {
        // Failure is surfaced to the user via `loginMutation.isError` (the alert below).
      }
    },
  });

  const loginErrorMessage =
    loginMutation.isError ?
      isAxiosError(loginMutation.error) && loginMutation.error.response?.status === 401 ?
        t`Invalid username or password.`
      : t`Login failed. Please try again.`
    : null;

  return (
    <div className='bg-muted/30 flex min-h-screen flex-col items-center justify-center gap-6 p-4'>
      <div className='bg-background w-full max-w-md space-y-6 rounded-xl border p-8 shadow-sm'>
        <div className='mb-10 flex flex-col items-center'>
          <img src='/logo_HD.png' alt='Rossini Energy' className='h-20 w-auto' />
          <h1 className='text-2xl font-semibold tracking-tight'>
            <Trans>Administration Panel</Trans>
          </h1>
        </div>

        {error === 'no_access' ?
          <div className='border-destructive/30 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm'>
            <Trans>
              This account doesn’t have park-admin access. Sign in with a park-admin account.
            </Trans>
          </div>
        : null}

        <form
          className='space-y-6'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field name='username'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Username</Trans>
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    autoComplete='username'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name='password'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Password</Trans>
                  </FieldLabel>
                  <PasswordInput
                    id={field.name}
                    name={field.name}
                    autoComplete='current-password'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </FieldGroup>

          {loginErrorMessage ?
            <p role='alert' className='text-destructive text-sm'>
              {loginErrorMessage}
            </p>
          : null}

          <div className='flex items-center justify-between gap-2'>
            <RecoveryDialog />
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(submitting) => (
                <Button type='submit' disabled={submitting}>
                  {submitting ? t`Signing in…` : t`Log in`}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </div>

      {isStaff ?
        <SudoLoginPanel />
      : null}
    </div>
  );
}

function RecoveryDialog() {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const recoveryMutation = useMutation({
    mutationFn: (username: string) =>
      api.post('/public/1/auth/passwordrecovery/create/', {
        username,
        newpwd_urlroot: `${window.location.origin}/recovery/`,
      }),
  });

  const recoverySchema = z.object({
    username: z.string().min(1, t`Username is required`),
  });

  const form = useForm({
    defaultValues: { username: '' },
    validators: { onSubmit: recoverySchema },
    onSubmit: async ({ value }) => {
      try {
        await recoveryMutation.mutateAsync(value.username);
        toast.success(t`A recovery email has been sent.`);
        setOpen(false);
        form.reset();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          toast.error(t`No account found with that username.`);
        } else {
          toast.error(t`Could not send the recovery email. Please try again.`);
        }
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant='link' className='px-0' />}>
        <Trans>Forgot password?</Trans>
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              <Trans>Reset your password</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Enter your username and we'll email you a recovery link.</Trans>
            </DialogDescription>
          </DialogHeader>

          <div className='py-4'>
            <form.Field name='username'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Username</Trans>
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    autoComplete='username'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(submitting) => (
                <Button type='submit' disabled={submitting}>
                  {submitting ? t`Sending…` : t`Send recovery email`}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
